import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  analyzeUploadedGlb,
  type GlbAnalysisStage,
  type GlbValidationReport,
} from "../../services/glb-analysis";
import type { ViewerSettings } from "../../types/settings";
import type { GlbAnalysisIssue } from "../../services/glb-analysis";
import { withOpacity } from "../../utils/colorUtils";

type UploadedModelLoadState =
  | { status: "idle" }
  | { status: "validated"; session: { fingerprint: string } }
  | { status: "loading"; pendingSession: { fingerprint: string } }
  | { status: "loaded"; activeSession: { fingerprint: string } }
  | {
      status: "load-error";
      pendingSession: { fingerprint: string };
      message: string;
    };

interface GlbUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadValidatedGlb: (file: File, report: GlbValidationReport) => void;
  language: ViewerSettings["language"];
  loadState?: UploadedModelLoadState;
  title?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

function stageLabel(stage: GlbAnalysisStage): string {
  switch (stage) {
    case "reading":
      return "Reading file";
    case "binary-validation":
      return "Validating binary";
    case "scene-parse":
      return "Parsing scene";
    case "animation-analysis":
      return "Inspecting animations";
    case "final-validation":
      return "Applying pipeline rules";
    case "complete":
      return "Validation complete";
    case "error":
      return "Validation failed";
    case "idle":
    default:
      return "Ready for pipeline ingest";
  }
}

function getIssueGroupLabel(issue: GlbAnalysisIssue): string {
  if (issue.code.startsWith("material."))
    return issue.severity === "warning" ? "Warnings" : "Materials";
  if (issue.code.startsWith("texture."))
    return issue.severity === "warning" ? "Warnings" : "Textures";
  if (issue.code.startsWith("extension."))
    return issue.severity === "warning" ? "Warnings" : "Extensions";
  if (issue.code.startsWith("animation.") || issue.code.startsWith("camera.")) {
    return issue.severity === "warning" ? "Warnings" : "Animations";
  }
  if (issue.code.startsWith("scene."))
    return issue.severity === "warning" ? "Warnings" : "Scene";
  if (issue.severity === "warning") return "Warnings";
  return "Other Issues";
}

function getIssueGuidance(issue: GlbAnalysisIssue): string | null {
  if (issue.code === "material.extension.rejected") {
    return "Update the Blender material to use core glTF PBR inputs or bake the unsupported effect before export.";
  }

  if (
    issue.code === "texture.slot.missing-image" ||
    issue.code === "texture.slot.missing-source"
  ) {
    return "Re-export the GLB after reconnecting or rebaking the missing texture image in Blender.";
  }

  if (issue.code === "texture.mime.unsupported") {
    return "Re-export the texture as PNG or JPEG for phase-1 pipeline compatibility.";
  }

  if (issue.code === "material.runtime.unsupported") {
    return "Simplify the Blender material to a supported Principled BSDF glTF export path.";
  }

  if (issue.code === "material.alpha.blend") {
    return "Use opaque materials in phase 1, or switch to a supported alpha-mask workflow if appropriate.";
  }

  if (
    issue.code === "camera.animation.count" ||
    issue.code === "camera.node.count"
  ) {
    return "Export exactly one authoritative animated camera path for the cinematic/manual pipeline.";
  }

  return null;
}

export function GlbUploadModal({
  isOpen,
  onClose,
  onLoadValidatedGlb,
  language,
  loadState = { status: "idle" },
  title = "Pipeline GLB Ingest",
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: GlbUploadModalProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [stage, setStage] = useState<GlbAnalysisStage>("idle");
  const [stageMessage, setStageMessage] = useState(stageLabel("idle"));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [report, setReport] = useState<GlbValidationReport | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const openTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(openTimer);
    }

    setIsVisible(false);
    const closeTimer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(closeTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const summaryLines = useMemo(() => {
    if (!report) return [];

    const lines = [
      `Scenes: ${report.summary.sceneCount === 1 ? "Single scene" : report.summary.sceneCount}`,
      `Camera Animations: ${report.summary.animatedCameraClipCount}`,
      `Animated Camera Nodes: ${report.summary.animatedCameraNodeCount}`,
      `Meshes: ${report.summary.meshCount}`,
      `Materials: ${report.summary.materialCount}`,
      `Textures: ${report.summary.textureCount}`,
    ];

    if (
      report.summary.standardMaterialCount != null ||
      report.summary.physicalMaterialCount != null ||
      report.summary.unlitMaterialCount != null
    ) {
      lines.push(
        `Materials: ${report.summary.standardMaterialCount ?? 0} standard / ${report.summary.physicalMaterialCount ?? 0} physical / ${report.summary.unlitMaterialCount ?? 0} unlit`,
      );
    }

    if (report.summary.alphaBlendMaterialCount != null) {
      lines.push(
        `Alpha Blend Materials: ${report.summary.alphaBlendMaterialCount}`,
      );
    }

    if (report.summary.doubleSidedMaterialCount != null) {
      lines.push(
        `Double-Sided Materials: ${report.summary.doubleSidedMaterialCount}`,
      );
    }

    return lines;
  }, [report]);

  const groupedIssues = useMemo(() => {
    if (!report) return [];

    const groups = new Map<string, GlbAnalysisIssue[]>();

    report.issues.forEach((issue) => {
      const label = getIssueGroupLabel(issue);
      const existing = groups.get(label) ?? [];
      existing.push(issue);
      groups.set(label, existing);
    });

    return [...groups.entries()];
  }, [report]);

  async function handleFileSelection(file: File | null) {
    setSelectedFile(file);
    setErrorMessage(null);
    setReport(null);

    if (!file) {
      setStage("idle");
      setStageMessage(stageLabel("idle"));
      return;
    }

    if (!file.name.toLowerCase().endsWith(".glb")) {
      setStage("error");
      setStageMessage(stageLabel("error"));
      setErrorMessage("Pipeline ingest must use the .glb extension.");
      return;
    }

    try {
      setStage("reading");
      setStageMessage(stageLabel("reading"));

      const nextReport = await analyzeUploadedGlb(file, {
        onProgress: (_value, label) => {
          const labelMap: Record<string, GlbAnalysisStage> = {
            "Reading file": "reading",
            "Validating binary": "binary-validation",
            "Parsing scene": "scene-parse",
            "Applying pipeline rules": "final-validation",
            "Validation complete": "complete",
          };
          const nextStage = labelMap[label] ?? "animation-analysis";
          setStage(nextStage);
          setStageMessage(label);
        },
      });

      setReport(nextReport);
      setStage(nextReport.status === "pass" ? "complete" : "error");
      setStageMessage(
        nextReport.status === "pass"
          ? "Validation complete"
          : "Validation failed",
      );
    } catch (error) {
      setStage("error");
      setStageMessage(stageLabel("error"));
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  }

  if (!shouldRender) return null;

  return createPortal(
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 250,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.25s ease-in-out",
        fontFamily,
        padding: "1.5rem",
      }}
      data-oid="3lvpuuz"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pipeline-glb-upload-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(640px, 92vw)",
          maxHeight: "88vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: "2rem",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.96)",
          transition: "transform 0.25s ease-in-out",
          color: primaryTextColor,
        }}
        data-oid=".vdl4hr"
      >
        <div
          style={{ padding: "1.5rem 2rem 1rem", position: "relative" }}
          data-oid="-otld_o"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close pipeline ingest modal"
            style={{
              position: "absolute",
              top: "1.25rem",
              right: "1.25rem",
              width: "2.5rem",
              height: "2.5rem",
              border: "none",
              borderRadius: "9999px",
              background: "transparent",
              color: secondaryTextColor,
              cursor: "pointer",
            }}
            data-oid="0hmoyz0"
          >
            <span className="material-symbols-outlined" data-oid="v-kirqs">
              close
            </span>
          </button>

          <div
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            data-oid="rnz-i_o"
          >
            <span
              className="material-symbols-outlined"
              style={{ color: accentColor }}
              data-oid="cgrmgcp"
            >
              upload_file
            </span>
            <h2
              id="pipeline-glb-upload-title"
              style={{ margin: 0, fontSize: "1.1rem" }}
              data-oid=".uba:sw"
            >
              {title}
            </h2>
          </div>
          <p
            style={{
              margin: "0.75rem 0 0",
              color: secondaryTextColor,
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
            data-oid="p1wdlg7"
          >
            This is a strict ingest gate for pipeline-authored GLBs. Passing
            glTF validity alone is not enough.
          </p>
        </div>

        <div
          style={{
            padding: "0 2rem 1.5rem",
            overflowY: "auto",
            display: "grid",
            gap: "1rem",
          }}
          data-oid="tnu1wu-"
        >
          <section
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: "1rem",
              padding: "1rem",
              background: isDragging
                ? withOpacity(accentColor, 0.08)
                : "rgba(255, 255, 255, 0.02)",
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              const nextFile = event.dataTransfer.files?.[0] ?? null;
              void handleFileSelection(nextFile);
            }}
            data-oid="yqziqwe"
          >
            <label
              htmlFor="glb-upload-input"
              style={{
                display: "block",
                fontWeight: 600,
                marginBottom: "0.75rem",
              }}
              data-oid="l.-qlm8"
            >
              Choose GLB file
            </label>
            <input
              id="glb-upload-input"
              type="file"
              accept=".glb,model/gltf-binary"
              onChange={(event) => {
                const nextFile = event.target.files?.[0] ?? null;
                void handleFileSelection(nextFile);
              }}
              data-oid="vzeg9ky"
            />

            <p
              style={{
                margin: "0.75rem 0 0",
                color: secondaryTextColor,
                fontSize: "0.85rem",
              }}
              data-oid=":ar1xww"
            >
              Drag and drop a pipeline-authored `.glb` here, or browse for a
              file.
            </p>
            {selectedFile ? (
              <p
                style={{
                  margin: "0.75rem 0 0",
                  color: secondaryTextColor,
                  fontSize: "0.85rem",
                }}
                data-oid="q2b6.i3"
              >
                Selected: {selectedFile.name}
              </p>
            ) : null}
          </section>

          <section
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: "1rem",
              padding: "1rem",
              background: "rgba(255, 255, 255, 0.02)",
            }}
            data-oid="56nw1.l"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                alignItems: "center",
              }}
              data-oid="-8j_gim"
            >
              <strong data-oid="ucef8co">Analysis Progress</strong>
              <span
                style={{ color: secondaryTextColor, fontSize: "0.85rem" }}
                data-oid="8s85mn:"
              >
                {stageMessage}
              </span>
            </div>
          </section>

          {errorMessage ? (
            <section
              style={{
                border: "1px solid rgba(244, 63, 94, 0.35)",
                borderRadius: "1rem",
                padding: "1rem",
                background: "rgba(244, 63, 94, 0.1)",
                color: "#fecdd3",
              }}
              data-oid="7q9kr04"
            >
              {errorMessage}
            </section>
          ) : null}

          {report ? (
            <>
              <section
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: "1rem",
                  padding: "1rem",
                  background: "rgba(255, 255, 255, 0.02)",
                }}
                data-oid="o:ycffz"
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.35rem 0.75rem",
                    borderRadius: "9999px",
                    background:
                      report.status === "pass"
                        ? "rgba(34, 197, 94, 0.14)"
                        : "rgba(244, 63, 94, 0.14)",
                    color: report.status === "pass" ? "#86efac" : "#fecdd3",
                    fontWeight: 700,
                    marginBottom: "0.75rem",
                  }}
                  data-oid="q:-hpad"
                >
                  {report.status === "pass" ? "PASS" : "FAIL"}
                </div>
                <div
                  style={{ fontSize: "0.95rem", marginBottom: "0.75rem" }}
                  data-oid="q:s5f_l"
                >
                  {report.status === "pass"
                    ? "This GLB passed pipeline compatibility validation."
                    : "This GLB is not compatible with the cinematic/manual pipeline."}
                </div>
                <div
                  style={{
                    display: "grid",
                    gap: "0.35rem",
                    color: secondaryTextColor,
                    fontSize: "0.85rem",
                  }}
                  data-oid="yuwd43x"
                >
                  {summaryLines.map((line) => (
                    <div key={line} data-oid="eq_yjjt">
                      {line}
                    </div>
                  ))}
                </div>
              </section>

              {report.issues.length > 0 ? (
                <section
                  style={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: "1rem",
                    padding: "1rem",
                    background: "rgba(255, 255, 255, 0.02)",
                  }}
                  data-oid="xce.67h"
                >
                  <strong
                    style={{ display: "block", marginBottom: "0.75rem" }}
                    data-oid="eebknw2"
                  >
                    Validation Issues
                  </strong>
                  <p
                    style={{
                      margin: "0 0 0.75rem",
                      color: secondaryTextColor,
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                    data-oid="7_b74ya"
                  >
                    These checks are meant to help asset authors correct Blender
                    exports before trying the ingest again.
                  </p>
                  <div
                    style={{ display: "grid", gap: "0.85rem" }}
                    data-oid="n8l0sme"
                  >
                    {groupedIssues.map(([groupLabel, issues]) => (
                      <section
                        key={groupLabel}
                        aria-label={groupLabel}
                        style={{ display: "grid", gap: "0.5rem" }}
                        data-oid="al1bhid"
                      >
                        <strong
                          style={{ fontSize: "0.9rem" }}
                          data-oid="3f-e-vi"
                        >
                          {groupLabel}
                        </strong>
                        <div
                          style={{ display: "grid", gap: "0.5rem" }}
                          data-oid=":4t9d9r"
                        >
                          {issues.map((issue) => {
                            const guidance = getIssueGuidance(issue);

                            return (
                              <div
                                key={`${issue.code}-${issue.message}-${issue.path ?? "no-path"}`}
                                style={{
                                  padding: "0.85rem",
                                  borderRadius: "0.75rem",
                                  background:
                                    issue.severity === "error"
                                      ? "rgba(244, 63, 94, 0.1)"
                                      : issue.severity === "warning"
                                        ? "rgba(245, 158, 11, 0.1)"
                                        : withOpacity(accentColor, 0.1),
                                  color: primaryTextColor,
                                  display: "grid",
                                  gap: "0.35rem",
                                }}
                                data-oid="_i34sn7"
                              >
                                <div
                                  style={{ fontWeight: 600 }}
                                  data-oid=".4adonc"
                                >
                                  {issue.message}
                                </div>
                                {issue.path ? (
                                  <div
                                    style={{
                                      color: secondaryTextColor,
                                      fontSize: "0.78rem",
                                    }}
                                    data-oid="z1vdbo9"
                                  >
                                    Source: {issue.path}
                                  </div>
                                ) : null}
                                {guidance ? (
                                  <div
                                    style={{
                                      color: secondaryTextColor,
                                      fontSize: "0.8rem",
                                      lineHeight: 1.45,
                                    }}
                                    data-oid="v515lq4"
                                  >
                                    Blender fix: {guidance}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : null}

          {loadState.status === "load-error" ? (
            <section
              style={{
                border: "1px solid rgba(244, 63, 94, 0.35)",
                borderRadius: "1rem",
                padding: "1rem",
                background: "rgba(244, 63, 94, 0.1)",
                color: "#fecdd3",
              }}
              data-oid="_w2ybti"
            >
              {loadState.message}
            </section>
          ) : null}
        </div>

        <div
          style={{
            padding: "1rem 2rem 1.5rem",
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
          }}
          data-oid="cafjeai"
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "0.85rem",
              border: `1px solid ${borderColor}`,
              background: "transparent",
              color: primaryTextColor,
              cursor: "pointer",
            }}
            data-oid="aiq..8z"
          >
            Close
          </button>
          <button
            type="button"
            disabled={!selectedFile || !report?.canLoadIntoScene}
            onClick={() => {
              if (!selectedFile || !report?.canLoadIntoScene) return;
              onLoadValidatedGlb(selectedFile, report);
            }}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "0.85rem",
              border: "none",
              background: report?.canLoadIntoScene
                ? accentColor
                : "rgba(255, 255, 255, 0.12)",
              color: report?.canLoadIntoScene ? "#0f172a" : secondaryTextColor,
              cursor: report?.canLoadIntoScene ? "pointer" : "not-allowed",
              fontWeight: 700,
            }}
            data-oid="acy5gz7"
          >
            Load Into Viewer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
